import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { aud } from '@/utils/format';
import {
  cashLedgerFor,
  cashLedgerId,
  dayPayouts,
  deferPayout,
  type DayPayout,
} from '@/domain/payoutFlow';
import type { DayPack, StudioId } from '@/domain/types';

/**
 * Day close (Prompt E4). The end-of-day ritual on the money screen: count the
 * till, record the variance against the E2 ledger, confirm the payouts are
 * signed, then freeze the day as a `DayPack` the office reads instead of live
 * data. Reopening is a logged, role-gated action — a closed day is never
 * silently edited. This is the ONE write path for close/reopen; the UI reads
 * `dayPackFor` / `unsignedPayoutArtists` and calls in here.
 */

/** Deterministic per-store+day id for a day pack (mirrors the cash-ledger id shape). */
export function dayPackId(studioId: StudioId, dayIso: string): string {
  return `${studioId}:${dayIso.slice(0, 10)}`;
}

/** The frozen pack for a day, if one has been closed (may be reopened). */
export function dayPackFor(studioId: StudioId, dayIso: string): DayPack | undefined {
  return useBrainStore.getState().dayPacks[dayPackId(studioId, dayIso)];
}

/** True once a day is closed and not since reopened. */
export function isDayClosed(studioId: StudioId, dayIso: string): boolean {
  const pack = dayPackFor(studioId, dayIso);
  return pack !== undefined && !pack.reopenedAt;
}

/** Cash expected in the till from the E2 ledger (float + cash-in − payouts). */
export function expectedTillAud(studioId: StudioId, dayIso: string): number {
  const l = cashLedgerFor(studioId, dayIso);
  return l.openingFloatAud + l.cashInAud - l.payoutsAud;
}

/** Artists whose envelope is payable but still unsigned — surfaced (not blocking) at close. */
export function unsignedPayoutArtists(studioId: StudioId, dayIso: string): DayPayout[] {
  return dayPayouts(studioId, dayIso).filter(
    (p) => p.payable && p.outstandingAud > 0 && p.record?.status !== 'deferred',
  );
}

/** Artists whose envelope the day could not cover and which was rolled whole. */
export function deferredPayoutArtists(studioId: StudioId, dayIso: string): DayPayout[] {
  return dayPayouts(studioId, dayIso).filter((p) => p.record?.status === 'deferred');
}

export interface CloseDayInput {
  studioId: StudioId;
  dayIso: string;
  countedAud: number;
  closedBy: string;
}

/**
 * Close the day: record the counted till + variance, freeze a `DayPack`
 * (payments, dockets, cash ledger, variance, any unsigned payouts) and stamp it.
 * Writes the counted cash onto the ledger so the reconciliation cash side closes.
 *
 * The close also SWEEPS: any envelope that is payable and still unpaid is written
 * as a deferral, so an unpaid day always leaves a record and tomorrow's arrears
 * list is complete even when nobody pressed anything (an artist is paid whole or
 * not at all — a day that went unpaid has to be visible as owed, not vanish).
 */
export function closeDay(input: CloseDayInput): DayPack {
  const { studioId, dayIso, countedAud, closedBy } = input;
  const brain = useBrainStore.getState();
  const now = useSimulationStore.getState().clockIso;
  const day = dayIso.slice(0, 10);

  const ledger = cashLedgerFor(studioId, dayIso);
  const expectedAud = ledger.openingFloatAud + ledger.cashInAud - ledger.payoutsAud;
  const varianceAud = expectedAud - countedAud;

  // Persist the count on the ledger so the reconciliation cash side ties out.
  const closedLedger = { ...ledger, countedAud };
  brain.upsertCashLedger(closedLedger);

  const payments = Object.values(brain.salePayments).filter(
    (p) => p.studioId === studioId && p.takenAt.slice(0, 10) === day,
  );
  const dockets = Object.values(brain.docketLines).filter(
    (d) => d.studioId === studioId && d.dayIso.slice(0, 10) === day,
  );
  // Sweep first: every payable-but-unpaid envelope becomes a deferral, so the pack
  // records what the day owes rather than losing it.
  const unsignedArtistIds = unsignedPayoutArtists(studioId, dayIso).map((p) => p.artistId);
  for (const artistId of unsignedArtistIds) {
    deferPayout({ studioId, artistId, dayIso, decidedBy: closedBy });
  }
  const deferredArtistIds = deferredPayoutArtists(studioId, dayIso).map((p) => p.artistId);

  const pack: DayPack = {
    id: dayPackId(studioId, day),
    studioId,
    dayIso: day,
    closedAt: now,
    closedBy,
    countedAud,
    expectedAud,
    varianceAud,
    unsignedArtistIds,
    deferredArtistIds,
    payments,
    dockets,
    cashLedger: closedLedger,
  };
  brain.upsertDayPack(pack);

  const varianceNote =
    varianceAud === 0
      ? 'till balanced'
      : `till ${varianceAud > 0 ? 'short' : 'over'} ${aud(Math.abs(varianceAud))}`;
  brain.appendActivity({
    id: `act-dayclose-${pack.id}`,
    type: 'day-closed',
    studioId,
    amount: countedAud,
    body: `${closedBy} closed ${day} · ${varianceNote}${
      deferredArtistIds.length
        ? ` · ${deferredArtistIds.length} envelope(s) deferred whole, owed tomorrow`
        : ''
    }. Pack sent to accounts.`,
    timestamp: now,
  });
  eventBus.publish('day:closed', { studioId, dayIso: day, varianceAud });
  return pack;
}

export interface ReopenDayInput {
  studioId: StudioId;
  dayIso: string;
  reopenedBy: string;
  reason: string;
}

/**
 * Reopen a closed day (Operations Lead / Owner only, enforced by the caller +
 * the route gate). Requires a reason; logged, never a silent edit. Keeps the
 * original close on the pack for audit and lifts the counted cash off the ledger
 * so the day returns to "pending count".
 */
export function reopenDay(input: ReopenDayInput): DayPack | undefined {
  const { studioId, dayIso, reopenedBy, reason } = input;
  if (reason.trim() === '') return undefined;
  const brain = useBrainStore.getState();
  const now = useSimulationStore.getState().clockIso;
  const pack = dayPackFor(studioId, dayIso);
  if (!pack) return undefined;

  const reopened: DayPack = { ...pack, reopenedAt: now, reopenedBy, reopenReason: reason.trim() };
  brain.upsertDayPack(reopened);

  // Lift the count so the day is genuinely open again (reconciliation → pending).
  const ledger = brain.cashLedgers[cashLedgerId(studioId, dayIso)];
  if (ledger) {
    const { countedAud: _dropped, ...rest } = ledger;
    void _dropped;
    brain.upsertCashLedger(rest);
  }

  brain.appendActivity({
    id: `act-dayreopen-${pack.id}-${now}`,
    type: 'day-reopened',
    studioId,
    body: `${reopenedBy} reopened ${pack.dayIso} · ${reason.trim()}`,
    timestamp: now,
  });
  eventBus.publish('day:reopened', { studioId, dayIso: pack.dayIso });
  return reopened;
}
